import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import { Breadcrumbs } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';

function SafetyPage() {
  const seo = pageMeta({
    title: 'Safety Tips',
    description:
      'Stay safe when renting, buying, or booking property in Nigeria. Essential safety tips from NaijaNest.',
    path: '/safety',
  });
  return (
    <div className="static-page safety-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Safety Tips', current: true },
        ]}
      />
      <section className="section safety-page__section" style={{ gridTemplateColumns: '1fr' }}>
        <header className="safety-page__intro" style={{ gridColumn: 'auto', width: '100%' }}>
          <h1>Safety Tips</h1>
          <p className="static-page__lead">
            Protect yourself when renting, buying, or booking properties in Nigeria.
          </p>
          <p>
            NaijaNest is a digital marketplace connecting property seekers with advertisers. We
            are not a party to any agreement between users, and we do not own, inspect, or
            negotiate the transactions that happen on the Platform. While we encourage
            transparency and responsible property advertising, users should always carry out
            their own checks before making decisions or payments. Follow these safety tips to
            help protect yourself when dealing with property listings.
          </p>
        </header>

        <div
          className="safety-page__news-grid"
          aria-label="Safety guidance sections"
          style={{ gridColumn: 'auto', width: '100%' }}
        >
          <article className="safety-page__block safety-page__block--lead">
            <h2>Before You Visit a Property</h2>
            <ul>
              <li>
                <strong>Research the location.</strong> Use tools such as Google Maps, satellite
                views, and local sources to understand the area, accessibility, and surrounding
                facilities.
              </li>
              <li>
                <strong>Verify the advertiser.</strong> Ask for a full name, contact details,
                business information where applicable, and CAC registration details for
                registered businesses.
              </li>
              <li>
                <strong>Compare prices.</strong> Be cautious of properties priced significantly
                below similar properties in the same area - unrealistically low prices may be a
                warning sign.
              </li>
              <li>
                <strong>Do not pay before viewing.</strong> Avoid sending deposits, booking fees,
                or advance payments before you have inspected the property and verified the
                relevant details.
              </li>
            </ul>
          </article>

          <article className="safety-page__block safety-page__block--compact">
            <h2>During the Property Visit</h2>
            <ul>
              <li>Visit during daylight hours where possible.</li>
              <li>Consider bringing someone with you.</li>
              <li>
                Confirm the property matches the listing - photos, description, location, and
                features advertised.
              </li>
              <li>
                Inspect important areas including water supply, electricity, plumbing, security
                arrangements, and access roads.
              </li>
              <li>
                Speak with neighbours or nearby residents where appropriate to understand the
                area and property history.
              </li>
            </ul>
          </article>

          <article className="safety-page__block safety-page__block--compact">
            <h2>Before Making Any Payment</h2>
            <ul>
              <li>
                <strong>Verify who you are dealing with.</strong> Confirm that the person
                advertising the property has the authority to sell, rent, or represent it.
              </li>
              <li>
                <strong>Avoid personal account payments where possible.</strong> For business
                transactions, request payments through verified business or corporate accounts.
              </li>
              <li>
                <strong>Request proper documentation.</strong> Obtain receipts and written
                agreements for all payments.
              </li>
              <li>
                <strong>For rentals:</strong> review and sign a tenancy agreement before payment,
                and understand all fees and obligations.
              </li>
              <li>
                <strong>For property purchases:</strong> conduct appropriate ownership and title
                checks, and engage a qualified legal professional to carry out searches and
                review documents.
              </li>
            </ul>
          </article>
        </div>

        <article
          className="safety-page__block safety-page__block--full"
          style={{ gridColumn: 'auto', width: '100%' }}
        >
          <h2>Common Warning Signs</h2>
          <div className="static-page__red-flags">
            <div className="static-page__red-flag">
              <strong>Refusal to allow inspection</strong>
              <p>
                Be cautious if someone refuses or continuously delays a physical inspection
                without a reasonable explanation.
              </p>
            </div>
            <div className="static-page__red-flag">
              <strong>Pressure to pay immediately</strong>
              <p>
                &quot;Someone else is ready to pay now&quot; or &quot;the property will be gone
                in a few hours&quot; - high-pressure tactics are commonly used in scams.
              </p>
            </div>
            <div className="static-page__red-flag">
              <strong>Prices far below market value</strong>
              <p>
                Compare similar properties in the same location. A significantly cheaper price
                may indicate a problem.
              </p>
            </div>
            <div className="static-page__red-flag">
              <strong>Missing or poor documentation</strong>
              <p>
                Be cautious if the advertiser cannot provide reasonable information about
                ownership, authority to advertise, agreements, or receipts.
              </p>
            </div>
            <div className="static-page__red-flag">
              <strong>Unusual upfront charges</strong>
              <p>
                Be careful with unexplained viewing fees, processing fees, reservation fees, or
                other unofficial charges - always understand what you are paying for.
              </p>
            </div>
          </div>
        </article>

        <article
          className="safety-page__block safety-page__block--full"
          style={{ gridColumn: 'auto', width: '100%' }}
        >
          <h2>Protect Yourself Online</h2>
          <p>Be cautious of:</p>
          <ul>
            <li>duplicate listings appearing under different names;</li>
            <li>listings using copied or cloned photos from other properties;</li>
            <li>people pretending to represent companies or agents (impersonation);</li>
            <li>
              requests to move the conversation off NaijaNest to another messaging app before
              you&rsquo;ve verified who you&rsquo;re dealing with - keep contact through NaijaNest
              Messages or a call until you&rsquo;re confident;
            </li>
            <li>requests for sensitive personal information.</li>
          </ul>
          <p>Never share your bank login details, passwords, security codes, or other unnecessary personal information.</p>
        </article>

        <article
          className="safety-page__block safety-page__block--full"
          style={{ gridColumn: 'auto', width: '100%' }}
        >
          <h2>Report Suspicious Activity</h2>
          <p>If you encounter a suspected fake listing, fraudulent behaviour, impersonation, or misleading information:</p>
          <ul>
            <li>
              Use the <strong>Report</strong> button on the listing itself - every property page
              has one - so we can review it in context.
            </li>
            <li>
              Or report it through our <Link to="/contact">Contact page</Link>, including the
              listing link, advertiser details, screenshots or supporting information, and a
              description of the issue.
            </li>
          </ul>
          <p>
            NaijaNest may review reports and take appropriate action, including removing listings
            or restricting accounts where necessary.
          </p>
        </article>

        <article
          className="safety-page__block safety-page__block--full"
          style={{ gridColumn: 'auto', width: '100%' }}
        >
          <h2>Remember</h2>
          <p>
            NaijaNest helps users discover and connect through property listings. Always verify
            information independently and make informed decisions before entering into any
            property transaction.
          </p>
        </article>
      </section>
    </div>
  );
}
export default SafetyPage;
